import { useCallback, useEffect, useMemo } from "react";

import { useDispatch, useSelector } from "metabase/lib/redux";
import { resetRowZoom, zoomInRow } from "metabase/query_builder/actions";
import type { ObjectId } from "metabase/visualizations/components/ObjectDetail/types";
import { isPK } from "metabase-lib/v1/types/utils/isa";
import type { DatasetData } from "metabase-types/api";
import type { State } from "metabase-types/store";

const getZoomedObjectId = (state: State) => state.qb?.zoomedRowObjectId;

/**
 * Resolve the row a table's "view details" button asked to expand.
 *
 * The shared table always reports that click by dispatching `zoomInRow` into
 * the query builder store (see `useObjectDetail` in `TableInteractive`), and
 * the query builder renders the detail view off the back of it. This workbench
 * doesn't own that store, so it observes the same key and renders the detail
 * view itself.
 *
 * The id is the value of the single primary key column when there is exactly
 * one — matching how `useObjectDetail` derives it — and the row index
 * otherwise, which is the usual case for native results.
 */
export function useResultObjectDetail(
  data: DatasetData | null,
  enabled: boolean,
) {
  const dispatch = useDispatch();
  const zoomedObjectId = useSelector(getZoomedObjectId);

  const primaryKeyIndex = useMemo(() => {
    if (!data) {
      return -1;
    }
    const primaryKeyColumns = data.cols.filter(isPK);
    return primaryKeyColumns.length === 1
      ? data.cols.indexOf(primaryKeyColumns[0])
      : -1;
  }, [data]);

  const getObjectId = useCallback(
    (rowIndex: number): ObjectId => {
      if (primaryKeyIndex < 0) {
        return rowIndex;
      }
      const value = data?.rows[rowIndex]?.[primaryKeyIndex];
      return typeof value === "number" || typeof value === "string"
        ? value
        : rowIndex;
    },
    [data, primaryKeyIndex],
  );

  const rowIndex = useMemo(() => {
    if (!enabled || !data || zoomedObjectId == null) {
      return -1;
    }
    if (primaryKeyIndex >= 0) {
      return data.rows.findIndex(
        (row) => row[primaryKeyIndex] === zoomedObjectId,
      );
    }
    return typeof zoomedObjectId === "number" &&
      zoomedObjectId >= 0 &&
      zoomedObjectId < data.rows.length
      ? zoomedObjectId
      : -1;
  }, [data, enabled, primaryKeyIndex, zoomedObjectId]);

  const close = useCallback(() => {
    dispatch(resetRowZoom());
  }, [dispatch]);

  const zoomToRow = useCallback(
    (nextRowIndex: number) => {
      dispatch(zoomInRow({ objectId: getObjectId(nextRowIndex) }));
    },
    [dispatch, getObjectId],
  );

  // The zoom lives in a store shared with the query builder, so make sure a
  // stale one can't pop the detail view open on an unrelated result.
  useEffect(() => {
    if (!enabled && zoomedObjectId != null) {
      dispatch(resetRowZoom());
    }
  }, [dispatch, enabled, zoomedObjectId]);

  useEffect(() => close, [close]);

  return {
    rowIndex,
    row: rowIndex >= 0 ? data?.rows[rowIndex] : undefined,
    close,
    zoomToRow,
  };
}
